import React__default, { createContext, useEffect, useMemo, useContext } from 'react';
import styled, { css } from 'styled-components';
import { setTestId } from '../../lib/testId.js';
import { HappyWebSystemError, useQueryChildComponents } from '../base/SystemComponent.js';
import { COLOR_PALETTE } from '../colors/colorPalette.js';
import { __Icon } from '../icon/Icon.js';
import { Box } from '../layout/Box.js';
import { Inline } from '../layout/Inline.js';
import { Link } from '../link/Link.js';
import { Menu, MenuButtonIcon } from '../menu/Menu.js';
import { Text } from '../text/Text.js';
import { TableSelectRow } from './Table.js';
import { ScrollHighlightLeft, ScrollHighlightRight } from './TableScrollHighlight.js';

const TableRowContext = createContext(undefined);
const useTableRow = ()=>{
    const context = useContext(TableRowContext);
    if (!context) {
        throw new HappyWebSystemError("useTableRow", "Missing TableRowContext - a TableRowContextProvider must be set.");
    }
    return context;
};
const TableRowProvider = ({ id , to , onClick , children  })=>{
    const value = useMemo(()=>{
        return {
            id,
            to,
            onClick
        };
    }, [
        id,
        to,
        onClick
    ]);
    return React__default.createElement(TableRowContext.Provider, {
        value: value
    }, children);
};
const isValidTableRowChild = (comp)=>{
    return comp?.type?.dataId === TableData.dataId || comp?.type?.dataId === TableOverflowMenu.dataId;
};
const hasTableOverflowMenuChild = (comp)=>{
    return comp?.type?.dataId === TableOverflowMenu.dataId;
};
const TableRow = ({ id , to , onClick , highlight =false , children , ...props })=>{
    useEffect(()=>{
        const data = React__default.Children.toArray(children);
        if (!isValidTableRowChild(data[0])) {
            throw new HappyWebSystemError("TableRow", "Only TableData and TableOverflowMenu elements are valid as children of TableRow.");
        }
    // Only check for component composition on first mount, not needed from then on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const hasOverflowMenu = useMemo(()=>{
        const data = React__default.Children.toArray(children);
        return hasTableOverflowMenuChild(data[data.length - 1]);
    }, [
        children
    ]);
    if (to !== undefined && onClick !== undefined) {
        throw new HappyWebSystemError("TableRow", "Must provide one of only either `to` or `onClick` props to TableRow.");
    }
    return React__default.createElement("tr", {
        "data-hpy-web-table-row": true,
        "data-hpy-web-table-highlight": highlight,
        ...setTestId("TableRow-" + id),
        ...props
    }, React__default.createElement(TableRowProvider, {
        id: id,
        to: to,
        onClick: onClick
    }, children, !hasOverflowMenu && React__default.createElement(TableDataSpacer, null)));
};
TableRow.dataId = "data-hpy-web-table-row";
TableRow.displayName = "TableRow";
const StyledLink = styled(Link)`
  height: 100%;
  position: absolute;
  top: 0;
  width: 100%;
  cursor: pointer;
  text-decoration: none;

  &:focus,
  &:hover {
    color: inherit;
  }
`;
const StyledButton = styled.button`
  height: 100%;
  position: absolute;
  top: 0;
  width: 100%;
  background: none;
  border: none;

  &:hover,
  &:focus {
    background: none;
    border: none;
    cursor: pointer;
  }
`;
const TABLE_DATA_PADDING_REM = 1.25;
const TableData = ({ align ="left" , alignY ="center" , stickyY ="none" , forwardedRef , children , ...props })=>{
    const [extras, data] = useQueryChildComponents(children, [
        TableSelectRow,
        TableIcon
    ]);
    const row = useTableRow();
    return React__default.createElement(StyledTD, {
        ref: forwardedRef,
        "data-hpy-web-table-data": true,
        stickyY: stickyY,
        ...props
    }, row?.to !== undefined && React__default.createElement(StyledLink, {
        testId: "Link-TableData-" + row.id,
        to: row.to
    }), row?.onClick !== undefined && React__default.createElement(StyledButton, {
        ...setTestId("Button-TableData-" + row.id),
        onClick: ()=>row?.onClick !== undefined && row.onClick(row.id)
    }), React__default.createElement(Box, {
        "data-hpy-web-table-data-inner": true,
        height: "fill",
        width: "fill",
        padding: [
            1,
            TABLE_DATA_PADDING_REM
        ],
        overflow: "hidden"
    }, React__default.createElement(Inline, {
        alignY: alignY,
        wrap: false,
        height: "fill",
        width: "fill",
        gap: 1,
        align: align
    }, extras, data.map((c, i)=>typeof c === "string" ? React__default.createElement(Text, {
            key: i,
            truncate: true,
            // The 'main info' column has a special highlight
            weight: stickyY !== "none" ? "bold" : "regular",
            size: stickyY !== "none" ? "large" : "medium"
        }, c) : c))), React__default.createElement(ScrollHighlightLeft, {
        "data-hpy-web-table-scrollhighlight-left": true
    }), React__default.createElement(ScrollHighlightRight, {
        "data-hpy-web-table-scrollhighlight-right": true
    }));
};
TableData.dataId = "data-hpy-web-table-data";
TableData.displayName = "TableData";
const TableIcon = (props)=>{
    return React__default.createElement(__Icon, {
        "data-hpy-web-table-icon": true,
        ...props,
        size: "medium"
    });
};
TableIcon.dataId = "data-hpy-web-table-icon";
TableIcon.displayName = "TableIcon";
const Spacer = styled.div`
  width: 2.5rem;
  height: 3rem;
`;
const TableDataSpacer = (props)=>{
    return React__default.createElement(TableData, {
        "data-hpy-web-table-overflow-menu": true,
        stickyY: "right",
        ...props
    }, React__default.createElement(Spacer, {
        "data-hpy-web-table-data-spacer": true
    }));
};
TableDataSpacer.dataId = "data-hpy-web-table-data-spacer";
TableDataSpacer.displayName = "TableDataSpacer";
const TableOverflowMenu = ({ testId ="X" , children  })=>{
    return React__default.createElement(TableData, {
        "data-hpy-web-table-overflow-menu": true,
        stickyY: "right"
    }, React__default.createElement(Menu, {
        testId: testId
    }, React__default.createElement(MenuButtonIcon, {
        testId: testId,
        variant: "default-inverse",
        icon: "overflow"
    }), children));
};
TableOverflowMenu.dataId = "data-hpy-web-table-overflow-menu";
TableOverflowMenu.displayName = "TableOverflowMenu";
const StyledTD = styled.td`
  position: relative;
  border: 0;
  padding: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: left;
  background-color: ${COLOR_PALETTE.white};

  z-index: 1;

  & [data-hpy-web-stack],
  & [data-hpy-web-text] {
    pointer-events: none;
  }

  & [data-hpy-web-combobox-field] {
    pointer-events: auto;
  }

  ${(props)=>{
    if (props.stickyY === "none") {
        return "";
    }
    if (props.stickyY === "left") {
        return css`
        position: sticky;
        left: 0;
        z-index: 2;
      `;
    }
    if (props.stickyY === "right") {
        return css`
        position: sticky;
        right: 0;
        z-index: 2;
      `;
    }
}}

  /* Allow the scroll highlight to appear */
  &:first-child, &[${TableOverflowMenu.dataId}] {
    overflow: visible;
  }
`;

export { TABLE_DATA_PADDING_REM, TableData, TableDataSpacer, TableIcon, TableOverflowMenu, TableRow, useTableRow };
